import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import * as Sentry from '@sentry/angular-ivy';
import { AppToastComponent, AppToastData, AppToastVariant } from './app-toast.component';
import { CapturedHttpError, peekRecentHttpError, takeRecentHttpError } from './http-error-capture.interceptor';

const ERROR_PATTERN = /fail|error|erro|falh|invalid|inv[aá]lid|not found|unable|could not|cannot|n[aã]o foi|n[aã]o [eé] poss[ií]vel/i;
const SUCCESS_PATTERN = /success|sucesso|updated|saved|deleted|removed|created|applied|atualizad|salv|exclu[ií]d|criad|conclu[ií]d/i;

/** "Failed", "Error!", "Erro" — avisos que não dizem o que aconteceu. */
const GENERIC_ERROR = /^\s*(failed|error|erro|falha|falhou)\s*[.!]*\s*$/i;

const DEFAULT_DURATION = 4000;

/**
 * O que a pessoa vê para cada tipo de falha: um título curto e a causa provável
 * com o que fazer. Nunca o método, a rota, o status HTTP ou o texto técnico do
 * servidor — esses vão só para o log (console + Sentry), com o código de suporte.
 */
interface ErrorCopy {
	title: string;
	message: string;
	/** Mostra o código de suporte (falha do servidor, a equipe precisa achar no log). */
	support?: boolean;
}

const SUPPORT_HINT = 'If it keeps happening, contact support.';

export function errorCopyFor(status: number): ErrorCopy {
	if (status === 0) {
		return { title: 'No connection', message: 'Could not reach the server. Check your internet connection and try again.' };
	}
	if (status === 400 || status === 422) {
		return { title: 'Check the information', message: 'Some of the information is invalid or incomplete. Review the fields and try again.' };
	}
	if (status === 401) {
		return { title: 'Session expired', message: 'Your session has expired. Reload the page and sign in again.' };
	}
	if (status === 403) {
		return { title: 'Access denied', message: 'You do not have permission for this action. Ask an administrator for access.' };
	}
	if (status === 404) {
		return { title: 'Not found', message: 'The item was not found. It may have been removed; refresh the page.' };
	}
	if (status === 409) {
		return { title: 'Conflict', message: 'This conflicts with data that already exists (it may be a duplicate or was changed by someone else). Refresh and try again.' };
	}
	if (status === 413) {
		return { title: 'File too large', message: 'The file or data sent is larger than allowed. Try a smaller file.' };
	}
	if (status === 408 || status === 504) {
		return { title: 'Request timed out', message: `The server took too long to respond. Wait a moment and try again. ${SUPPORT_HINT}`, support: true };
	}
	if (status === 502 || status === 503) {
		return { title: 'Service unavailable', message: `The system is temporarily unavailable. Wait a moment and try again. ${SUPPORT_HINT}`, support: true };
	}
	if (status >= 500) {
		return { title: 'Something went wrong', message: 'An unexpected error occurred on the server. Please contact support and quote the code below.', support: true };
	}
	return { title: 'Error', message: `This action could not be completed. Try again. ${SUPPORT_HINT}` };
}

function variantOf(message: string): AppToastVariant {
	if (ERROR_PATTERN.test(message)) return 'error';
	if (SUCCESS_PATTERN.test(message)) return 'success';
	return 'info';
}

function shortUrl(url: string): string {
	try {
		const { pathname } = new URL(url, window.location.origin);
		return pathname.replace(/^.*?\/api\//, '/');
	} catch {
		return url;
	}
}

/** Monta título, mensagem e detalhe do aviso a partir do que a tela pediu. */
export function buildToastData(message: string, action?: string, config?: MatSnackBarConfig): AppToastData {
	const text = (message ?? '').toString().trim();
	// A tela que repassa o texto do servidor (`err.error.message`) não diz "erro":
	// "intervals must not overlap" saía como aviso informativo.
	const recent = peekRecentHttpError();
	const echoesFailure = !!recent && (!text || text === recent.message);
	const variant = echoesFailure ? 'error' : variantOf(text);
	const hasAction = !!action;

	if (variant !== 'error') {
		return {
			variant,
			title: variant === 'success' ? 'Success' : 'Notice',
			message: text,
			actionLabel: action || null,
			hasAction,
		};
	}

	// Erro: o motivo real vem da última resposta da API, quando ela acabou de falhar.
	const http = takeRecentHttpError();
	const generic = GENERIC_ERROR.test(text);

	if (!http) {
		// Erro da própria tela (validação local): o texto dela já diz o que fazer.
		return {
			variant,
			title: 'Error',
			message: generic || !text || isTechnical(text) ? errorCopyFor(-1).message : text,
			detail: null,
			actionLabel: 'Confirm',
			hasAction,
		};
	}

	const copy = errorCopyFor(http.status);
	const raw = http.message && !/^ocorreu um erro inesperado\.?$/i.test(http.message) ? http.message : null;
	// Só erro de quem usa (4xx) mostra o texto do servidor — é a causa (ex.: "intervals must
	// not overlap"). Em 5xx o texto do servidor é interno e nunca vai para a tela.
	const backend = raw && http.status >= 400 && http.status < 500 && !isSensitive(raw) ? raw : null;
	const reference = copy.support || (raw && !backend) ? newReference() : null;
	if (reference) {
		// Detalhe completo só no log (console + Sentry), com o mesmo código do aviso.
		reportToLog(reference, http, text);
	}

	const reason = backend ?? copy.message;
	// A tela às vezes repassa o texto cru (`err.error.message`, `err.message`): em falha do
	// servidor ou texto técnico ele é descartado e fica só a causa provável.
	const screenIsRaw = (raw && text === raw) || isTechnical(text) || (http.status >= 500 && !!raw && text.includes(raw));
	const body = generic || !text || screenIsRaw || text === backend ? reason : `${text}\n${reason}`;

	return {
		variant,
		title: copy.title,
		message: body,
		detail: reference ? `Support code: ${reference}` : null,
		actionLabel: 'Confirm',
		hasAction,
	};
}

/**
 * Mensagem que expõe o interior do sistema: SQL, Prisma, stack trace, caminho de
 * arquivo, conexão, credencial. Essa não vai para a tela; vai para o log.
 */
const SENSITIVE_PATTERN = new RegExp(
	[
		'prisma', 'invocation', '\\bsql\\b', 'select\\s.+\\sfrom', 'insert into', 'update\\s.+\\sset', 'constraint',
		'relation ".+"', 'column ".+"', 'syntax error', 'ECONN', 'ETIMEDOUT', 'ENOTFOUND', 'socket',
		'\\bat\\s+\\S+\\s+\\(', 'node_modules', '/usr/app', '\\.ts:\\d+', '\\.js:\\d+', 'stack',
		'password', 'passwd', 'secret', 'token', 'bearer', 'authorization', 'connection string',
		'postgres', 'redis', 'camunda', 'engine-rest', 'keycloak', 'TypeError', 'ReferenceError',
		'Cannot read propert', 'undefined', 'null value',
	].join('|'),
	'i',
);

/**
 * O limite é por linha, não pelo total: o retorno de importação de planilha
 * ("Linha 5: "dip" não é um número válido", até 10 linhas, ou a lista de colunas
 * aceitas) passava de 300 caracteres e era escondido como se fosse stack trace —
 * a pessoa via "contate o suporte" em vez da linha a corrigir.
 */
function isSensitive(message: string): boolean {
	if (message.length > 2000 || SENSITIVE_PATTERN.test(message)) return true;
	return message.split('\n').some((line) => line.length > 400);
}

/** Textos que o Angular/Nest geram sozinhos e que não dizem nada para quem usa. */
const TECHNICAL_TEXT = /internal server error|http failure|unknown error|bad gateway|gateway time-?out|service unavailable|\b[45]\d\d\b.*\b(ok|error)\b/i;

function isTechnical(text: string): boolean {
	return !!text && (TECHNICAL_TEXT.test(text) || isSensitive(text));
}

/** Código curto para a pessoa passar ao suporte e alguém achar o erro no log. */
function newReference(): string {
	const time = Date.now().toString(36).slice(-5).toUpperCase();
	const rand = Math.random().toString(36).slice(2, 5).toUpperCase();
	return `ERR-${time}${rand}`;
}

function reportToLog(reference: string, http: CapturedHttpError, screenMessage: string): void {
	const context = { reference, screenMessage, method: http.method, url: http.url, status: http.status, backendMessage: http.message };
	console.error(`[app-toast] ${reference}`, context);
	try {
		Sentry.captureMessage(`HTTP ${http.status} ${http.method} ${shortUrl(http.url)} (${reference})`, {
			level: 'error',
			tags: { reference },
			extra: context,
		});
	} catch {
		// Sem Sentry configurado o console basta.
	}
}

let installed = false;

/**
 * Aviso com `panelClass` próprio (os badges coloridos, como o vermelho de
 * validação) mantém o visual dele. Todo o resto, a faixa preta padrão do meio da
 * tela, vira o cartão.
 */
function hasOwnStyle(config?: MatSnackBarConfig): boolean {
	return !!config?.panelClass && [config.panelClass].flat().filter(Boolean).length > 0;
}

/**
 * Todo `snackBar.open(...)` sem estilo próprio passa a abrir o AppToastComponent
 * no canto inferior direito, em vez da faixa preta padrão no meio da tela.
 *
 * Vai no protótipo porque o MatSnackBar do Angular Material 14 é fornecido por
 * módulo (`providedIn: MatSnackBarModule`): trocar o provider na raiz não chega
 * às telas lazy, e editar as ~1.400 chamadas uma a uma não é viável. Os avisos
 * com `panelClass` próprio seguem pelo `open` original do Material.
 *
 * Erro fica na tela até a pessoa confirmar ou fechar; os demais somem sozinhos.
 */
export function installAppToast(): void {
	if (installed) return;
	installed = true;

	const originalOpen = MatSnackBar.prototype.open;

	MatSnackBar.prototype.open = function (
		this: MatSnackBar,
		message: string,
		action = '',
		config?: MatSnackBarConfig,
	): MatSnackBarRef<TextOnlySnackBar> {
		if (hasOwnStyle(config)) {
			return originalOpen.call(this, message, action, config);
		}
		const data = buildToastData(message, action, config);
		const ref = this.openFromComponent(AppToastComponent, {
			...config,
			data,
			duration: data.variant === 'error' ? undefined : (config?.duration ?? DEFAULT_DURATION),
			horizontalPosition: 'end',
			verticalPosition: 'bottom',
			panelClass: ['app-toast-panel'],
			politeness: data.variant === 'error' ? 'assertive' : 'polite',
		});
		return ref as unknown as MatSnackBarRef<TextOnlySnackBar>;
	};
}
